/**
 * 估价信息
 * 
 * @Author: Focci
 * @Date: 2023-08-10 16:42:11
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-10 16:42:11
 */

import PropTypes from 'prop-types'
import SVGStarEmpty from '@img/star-empty.svg'
import SVGStar from '@img/star.svg'
import localeProgress from '@img/locale-progress.png'
import Rating from '@mui/material/Rating'
import clsx from 'clsx'
import Image from 'next/image'
import { isAU } from '@lib/utils'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

function EstimateInfoItem({
  label = '',
  value = '',
  className = '',
  chipClassName = 'text-c66 bg-cf2 px-2',
  textClassName = 'top-full'
}) {
  return (
    <div 
      className={clsx([
        'absolute top-1/2 -translate-y-1/2 z-1 flex flex-col gap-1',
        className
      ])}
    >
      <span 
        className={clsx([
          'h-4 flex items-center gap-1 text-f.6',
          chipClassName
        ])}
      >
        {label}
      </span>
      <b className={clsx(['text-base absolute text-c3b', textClassName])}>
        {value}
      </b>
    </div>
  )
}

function EstimateInfoRatingNZ({ data }) {
  const th = useTranslations('house')
  return (
    <div className="flex flex-col items-center">
      <h3 className="font-bold">
        {th('estimateAccuracy')}
      </h3>
      <div className="mt-2">
        <Rating 
          value={data?.confidence} 
          readOnly
          icon={<span className="mx-0.5 text-primary"><SVGStar width=".9rem" /></span>}
          emptyIcon={<span className="mx-0.5 text-primary"><SVGStarEmpty width=".9rem" /></span>} 
        />
      </div>
    </div>
  )
}

function EstimateInfoRatingAU({ data }) {
  const th = useTranslations('house')
  const [percent, iconPercent, labelColor] = useMemo(() => {
    const t = String(data?.confidence_label).toLowerCase()
    const c = 'text-[#ea7924]'

    return t === 'low'
      ? ['5%', '5%', [c]]
      : t === 'medium'
        ? ['50%', '50%', ['', c]]
        : ['100%', '99%', ['', '', c]]
  }, [data])
  
  return (
    <>
      <div className="text-center text-helper -mt-6">{th('estimateAccuracy')}</div>
      <div className="max-w-[15rem] mx-auto mt-6">
        <div className="relative mt-2">
          <div className="h-[.2rem] rounded-full bg-[#ccc]" />
          <div 
            className="h-[.2rem] rounded-full bg-[#ea7924] absolute top-0 left-0"
            style={{ width: percent }}
          />
          <div
            className="absolute -bottom-full mb-3 -ml-2 w-[.7rem] h-[1.1rem]"
            style={{ left: iconPercent }}
          >
            <Image src={localeProgress || ''} width={14} height={22} alt="." />
          </div>
        </div>
        <div className="grid grid-cols-3 mt-1 text-helper font-bold">
          <span className={labelColor[0]}>{th('lower')}</span>
          <span className={clsx(['text-center', labelColor[1]])}>{th('middle')}</span>
          <span className={clsx(['text-right', labelColor[2]])}>{th('hight')}</span>
        </div>
      </div>
    </>
  )
}

export default function EstimateInfo({
  data = {},
}) {
  const th = useTranslations('house')
  
  return (
    <div className="bg-cf2 p-4 rounded-sm">
      <div className="flex justify-between min-h-[7.5rem] relative mt-4">
        <EstimateInfoItem 
          label={th('low')}
          value={data?.low}
          className="pr-4 items-start"
        />
        <EstimateInfoItem 
          label={th('medium')}
          value={data?.avm_value}
          className="left-1/2 -translate-x-1/2 px-4 items-center flex-col-reverse"
          textClassName="!text-[2rem] mb-6 bottom-full"
          chipClassName="text-primary text-f.8 font-medium bg-cf2 px-2"
        />
        <EstimateInfoItem 
          label={th('high')}
          value={data?.high}
          className="right-0 pl-4 items-end"
        />
        <span className="absolute left-0 right-0 top-1/2 border-t border-[#c8c8c8] -mt-px" />
      </div>
      {isAU 
        ? <EstimateInfoRatingAU data={data} />
        : <EstimateInfoRatingNZ data={data} />}
    </div>
  )
}

EstimateInfo.propTypes = {
  data: PropTypes.object,
}
